import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowDown, ChevronRight } from 'lucide-react';
import { caseStudies, filterCategories, processSteps, stats } from '@/data/caseStudiesData';

/* ─── animation variants ─── */
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

/* ─── Animated Counter ─── */
function AnimatedCounter({ value, suffix, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const step = Math.max(1, Math.floor(value / (duration / 16)));
    const timer = setInterval(() => {
      start += step;
      if (start >= value) { setCount(value); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value]);

  return (
    <div ref={ref} className="text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight">
        {count}{suffix}
      </span>
      <p className="text-sm tracking-widest uppercase text-muted-foreground mt-3">{label}</p>
    </div>
  );
}

/* ─── Project Card ─── */
function ProjectCard({ project, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/case-studies/${project.slug}`}
        className="group block"
        data-testid={`project-card-${project.slug}`}
      >
        <div className="relative overflow-hidden bg-secondary mb-6">
          <div className="aspect-[3/4] overflow-hidden">
            <img
              src={project.heroImage}
              alt={project.client}
              className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
              loading="lazy"
            />
          </div>
          {/* Overlay */}
          <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-colors duration-300 flex items-end justify-center p-6">
            <span className="bg-butter text-foreground px-6 py-2.5 rounded-full text-xs tracking-widest uppercase font-medium">
              View More
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs tracking-widest uppercase text-muted-foreground">{project.industry}</span>
            <span className="w-1 h-1 rounded-full bg-muted-foreground" />
            <span className="text-xs tracking-widest uppercase text-muted-foreground">{project.category[0]}</span>
          </div>
          <h3 className="font-serif text-2xl md:text-3xl font-medium group-hover:text-butter-dark transition-colors duration-300">
            {project.client}
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-lg">{project.tagline}</p>
        </div>
      </Link>
    </motion.div>
  );
}

/* ─── Process Step (extracted to avoid hook-in-loop) ─── */
function ProcessStep({ step, index, total }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      className="relative text-center"
      data-testid={`process-step-${step.num}`}
    >
      <div className="w-14 h-14 rounded-full bg-butter flex items-center justify-center mx-auto mb-4 relative z-10">
        <span className="font-serif text-lg font-medium">{step.num}</span>
      </div>
      <h3 className="font-serif text-xl font-medium mb-2">{step.title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
      {index < total - 1 && (
        <div className="md:hidden flex justify-center my-4">
          <ChevronRight className="h-4 w-4 text-muted-foreground rotate-90" />
        </div>
      )}
    </motion.div>
  );
}

/* ─── Main Page ─── */
export default function CaseStudiesPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const nonFlagship = caseStudies.filter(p => !p.flagship);
  const filtered = activeFilter === 'All'
    ? nonFlagship
    : nonFlagship.filter(p =>
        p.category.includes(activeFilter) ||
        p.industry === activeFilter
      );

  return (
    <div className="pt-32" data-testid="case-studies-page">

      {/* ════════════ HERO ════════════ */}
      <section ref={heroRef} className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="cs-hero">
        {/* Animated background images */}
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
          <div className="absolute inset-0 grid grid-cols-3 gap-1 opacity-[0.08]">
            {caseStudies.slice(0, 6).map((p, i) => (
              <motion.div
                key={i}
                className="overflow-hidden"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 20 + i * 3, repeat: Infinity, ease: 'linear' }}
              >
                <img src={p.heroImage} alt="" className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 text-center px-6">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeInUp} className="text-sm tracking-[0.3em] uppercase text-muted-foreground mb-6">
              Muse Social Creative
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-5xl sm:text-6xl lg:text-8xl font-medium leading-[1.05] tracking-tight mb-10">
              Case Studies
            </motion.h1>
            <motion.div variants={fadeInUp} className="space-y-1">
              {['Real brands.', 'Real strategy.', 'Real creative.', 'Real results.'].map((line, i) => (
                <motion.p
                  key={i}
                  className="text-muted-foreground text-base md:text-lg"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                >
                  {line}
                </motion.p>
              ))}
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            className="mt-16 flex flex-col items-center gap-2 text-muted-foreground"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <span className="text-xs tracking-widest uppercase">Scroll</span>
            <ArrowDown className="h-4 w-4" />
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════ FEATURED: CITY OF MIAMI BEACH ════════════ */}
      <section className="relative bg-foreground text-background overflow-hidden" data-testid="cs-featured">
        {/* Hero image */}
        <div className="relative w-full h-[50vh] md:h-[60vh]">
          <img src="/images/mb-hero.webp" alt="City of Miami Beach" className="w-full h-full object-cover object-[center_40%]" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-foreground" />
        </div>
        {/* Content */}
        <div className="container-main pb-24 md:pb-32 pt-12">
          <Link to="/case-studies/city-of-miami-beach" className="group block max-w-6xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeInUp} className="text-xs tracking-[0.4em] uppercase text-butter mb-6">Featured Case Study</motion.p>
              <motion.h2 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-7xl font-medium text-background leading-[1.08] mb-6 group-hover:text-butter transition-colors duration-500">
                City of Miami Beach
              </motion.h2>
              <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-8 flex-wrap">
                {['Creative Direction', 'Campaign Strategy', 'Public Awareness'].map((s, i) => (
                  <span key={i} className="text-xs tracking-widest uppercase text-background/40">
                    {i > 0 && <span className="mr-3">•</span>}{s}
                  </span>
                ))}
              </motion.div>
              <motion.p variants={fadeInUp} className="text-background/50 text-base md:text-lg leading-relaxed max-w-3xl mb-10">
                Transforming one of America's biggest public safety challenges into a campaign that captured worldwide attention — generating 6.5B+ impressions and coverage from The New York Times, Bloomberg, NBC and Good Morning America.
              </motion.p>
              <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12 max-w-3xl">
                {[
                  { val: '6.5B+', label: 'Impressions' },
                  { val: '674K+', label: 'Website Visits' },
                  { val: '2B+', label: 'Video Views' },
                  { val: '123K+', label: 'Link Clicks' },
                ].map((m) => (
                  <div key={m.label} className="text-center">
                    <span className="font-serif text-3xl sm:text-4xl font-medium text-butter block mb-1">{m.val}</span>
                    <span className="text-[10px] tracking-widest uppercase text-background/40">{m.label}</span>
                  </div>
                ))}
              </motion.div>
              <motion.span variants={fadeInUp} className="inline-flex items-center gap-2 text-butter text-sm tracking-widest uppercase group-hover:gap-4 transition-all duration-300">
                View Full Case Study <ArrowRight className="h-4 w-4" />
              </motion.span>
            </motion.div>
          </Link>
        </div>
      </section>
      <section className="py-24 md:py-32" data-testid="cs-intro">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={stagger}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-8">
              Every project tells a different story.
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Every brand has different goals, audiences and challenges. Our role is to uncover the right strategy,
              execute exceptional creative, and build systems that create long-term growth.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-sm mt-6 tracking-wide">
              Below are selected projects showcasing our work across strategy, content production, social media, influencer marketing and brand development.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ════════════ FEATURED: AUDE SWIM ════════════ */}
      <section className="py-16 md:py-20 border-t border-b border-border" data-testid="cs-featured-aude">
        <div className="container-main">
          <Link to="/case-studies/aude-swim" className="group block max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <motion.div variants={fadeInUp}>
                <p className="text-xs tracking-[0.3em] uppercase text-[#C8A96A] mb-4">Photography Case Study</p>
                <h3 className="font-serif text-3xl sm:text-4xl font-medium leading-tight mb-4 group-hover:text-[#C8A96A] transition-colors duration-300">Aude Swim</h3>
                <p className="text-muted-foreground text-base leading-relaxed mb-6">Photography that performed — a retrospective analysis of measurable Instagram engagement across 50 published photographs.</p>
                <div className="flex items-center gap-8 mb-6">
                  {[{ val: '3,429', label: 'Top Likes' }, { val: '517', label: 'Avg Likes' }, { val: '50', label: 'Photos' }].map((m) => (
                    <div key={m.label} className="text-center">
                      <span className="font-serif text-2xl font-medium text-[#C8A96A] block">{m.val}</span>
                      <span className="text-[10px] tracking-widest uppercase text-muted-foreground">{m.label}</span>
                    </div>
                  ))}
                </div>
                <span className="inline-flex items-center gap-2 text-[#C8A96A] text-sm tracking-widest uppercase group-hover:gap-4 transition-all duration-300">
                  View Case Study <ArrowRight className="h-4 w-4" />
                </span>
              </motion.div>
              <motion.div variants={fadeInUp} className="relative overflow-hidden">
                <img src="/images/aude-post-9.jpg" alt="Aude Swim Campaign" className="w-full h-auto object-contain group-hover:scale-[1.03] transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-background">
                  <p className="font-serif text-4xl md:text-5xl font-medium text-[#C8A96A] mb-1">23,800</p>
                  <p className="text-[10px] tracking-widest uppercase text-background/60">Visible Photo Likes</p>
                </div>
              </motion.div>
            </motion.div>
          </Link>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ════════════ FILTERS ════════════ */}
      <section className="py-12" data-testid="cs-filters">
        <div className="container-main">
          <div className="flex flex-wrap justify-center gap-2">
            {filterCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-5 py-2 text-xs tracking-widest uppercase rounded-full border transition-all duration-300 ${
                  activeFilter === cat
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-transparent text-muted-foreground border-border hover:border-foreground hover:text-foreground'
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ PROJECT GRID ════════════ */}
      <section className="py-16 md:py-24" data-testid="cs-projects">
        <div className="container-main">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-20"
            >
              {filtered.map((project, i) => (
                <ProjectCard key={project.slug} project={project} index={i} />
              ))}
            </motion.div>
          </AnimatePresence>

          {filtered.length === 0 && (
            <p className="text-center text-muted-foreground py-20">No projects match this filter.</p>
          )}
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ════════════ PROCESS / TIMELINE ════════════ */}
      <section className="py-24 md:py-32" data-testid="cs-process">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Our Process</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight">
              How Muse Works
            </motion.h2>
          </motion.div>

          <div className="relative max-w-5xl mx-auto">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-border -translate-y-1/2" />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
              {processSteps.map((step, i) => (
                <ProcessStep key={step.num} step={step} index={i} total={processSteps.length} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ════════════ STATISTICS ════════════ */}
      <section className="py-24 md:py-32 bg-foreground text-background" data-testid="cs-stats">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-background/50 mb-4">By the Numbers</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-background">
              Impact at Scale
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-12 md:gap-6 max-w-5xl mx-auto">
            {stats.map((stat) => (
              <AnimatedCounter key={stat.label} {...stat} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ CTA ════════════ */}
      <section className="py-24 md:py-32" data-testid="cs-cta">
        <div className="container-main text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto">
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-6">
              Ready to build something exceptional?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed mb-10">
              Let's talk about your brand, your goals and how Muse can help you get there.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link
                to="/book-a-call"
                className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-10 py-4 rounded-full hover:bg-butter-dark transition-colors duration-300"
                data-testid="cs-cta-btn"
              >
                Start Your Project <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
